import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Badge, Button, cn } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import type { FlowGraph, FlowRun, FlowRunState } from '@/features/flows/types.ts'
import { useFlowRuns } from './useFlowRuns.ts'
import { RunDetail } from './RunDetail.tsx'

/**
 * What this flow has actually done (D12).
 *
 * **A run is read against the graph that produced it**, which is why the panel
 * is here rather than on the canvas: `flow_runs.graph_version` records the graph
 * a run executed, and the step log resolves to node *keys* that may since have
 * been retired. The detail joins `steps[]` to the current graph by `nodeId` and
 * falls back to the stored `key` when a node is gone, so a run of a deleted step
 * still reads as a sentence instead of an id.
 *
 * Selecting a run lifts its touched nodes to the caller, which passes them to
 * the canvas as a highlight — the panel never reaches into the canvas itself,
 * so "which run am I looking at" stays one piece of state in one place.
 */

const STATE_TONE: Record<FlowRunState, 'primary' | 'success' | 'danger'> = {
  running: 'primary',
  done: 'success',
  failed: 'danger',
}

interface RunsPanelProps {
  pipelineId: PipelineId
  flowId: string
  graph: FlowGraph
  selectedRunId: string | null
  onSelectRun: (runId: string | null) => void
  /** The nodes the selected run touched, lifted so the canvas can draw them. */
  onTouchedNodes: (nodeIds: readonly string[]) => void
}

export function RunsPanel({
  pipelineId,
  flowId,
  graph,
  selectedRunId,
  onSelectRun,
  onTouchedNodes,
}: RunsPanelProps) {
  const { t } = useTranslation()
  const feed = useFlowRuns(pipelineId, flowId)

  return (
    <section className="flex min-h-[12rem] flex-col rounded-xl border border-border bg-background-secondary lg:w-80 lg:shrink-0">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
          {t('pages.flows.runs.title', { defaultValue: 'Runs' })}
        </span>
        {feed.live && (
          <LoaderCircle
            className="h-3 w-3 animate-spin text-muted"
            aria-label={t('pages.flows.runs.live', { defaultValue: 'A run is in progress' })}
          />
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {feed.isLoading && (
          <p className="px-3 py-3 text-xs text-muted">
            {t('pages.flows.runs.loading', { defaultValue: 'Loading runs…' })}
          </p>
        )}

        {!feed.isLoading && feed.error !== undefined && (
          <p className="px-3 py-3 text-xs text-toast-error-text">
            {t('pages.flows.runs.error', {
              defaultValue: 'The engine returned an error while loading runs.',
            })}
          </p>
        )}

        {!feed.isLoading && feed.error === undefined && feed.runs.length === 0 && (
          <p className="px-3 py-3 text-xs italic text-muted">
            {t('pages.flows.runs.none', {
              defaultValue: 'This automation has not run yet.',
            })}
          </p>
        )}

        <ul>
          {feed.runs.map((run) => (
            <li key={run.id}>
              <RunRow
                run={run}
                selected={run.id === selectedRunId}
                onSelect={() => onSelectRun(run.id === selectedRunId ? null : run.id)}
              />
              {run.id === selectedRunId && (
                <RunDetail
                  pipelineId={pipelineId}
                  flowId={flowId}
                  runId={run.id}
                  graph={graph}
                  onTouchedNodes={onTouchedNodes}
                />
              )}
            </li>
          ))}
        </ul>

        {feed.nextCursor !== null && (
          <div className="px-3 py-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              disabled={feed.loadingOlder}
              onClick={feed.loadOlder}
            >
              {feed.loadingOlder
                ? t('pages.flows.runs.loadingOlder', { defaultValue: 'Loading…' })
                : t('pages.flows.runs.older', { defaultValue: 'Load older' })}
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  run: FlowRun
  selected: boolean
  onSelect: () => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={selected}
      className={cn(
        'flex w-full flex-col gap-1 border-b border-border/50 px-3 py-2 text-left transition-colors',
        'outline-none focus-visible:bg-sidebar-hover-bg',
        selected ? 'bg-primary-light' : 'hover:bg-sidebar-hover-bg',
      )}
    >
      <div className="flex items-center gap-2">
        <Badge tone={STATE_TONE[run.state]} size="sm">
          {t(`pages.flows.runs.state.${run.state}`, { defaultValue: run.state })}
        </Badge>
        <span className="ml-auto text-[10px] text-muted">
          {run.startedAt.toLocaleString(i18n.language)}
        </span>
      </div>

      {run.attempts > 1 && (
        <span className="text-[10px] text-muted">
          {t('pages.flows.runs.attempts', {
            count: run.attempts,
            defaultValue: '{{count}} attempts',
          })}
        </span>
      )}

      {/* The engine's own code, not a sentence: `flow_runs.error` is machine
          readable, and translating it here would invent a message the log does
          not contain. The step that failed carries the explanation. */}
      {run.error !== null && (
        <span className="break-all font-mono text-[10px] text-toast-error-text">{run.error}</span>
      )}
    </button>
  )
}
